// Sidebar.js - Updated to properly handle active state
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  ListItemIcon,
  Box,
  Divider,
  Typography,
  ListItemAvatar,
  Chip
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Settings,
  History,
  Search,
  AdminPanelSettings,
  LiveTv,
  LibraryBooks,
  Event,
  Flag
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Sidebar = ({ drawerWidth = 260 }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({
    settings: false
  });
  const [flagErrors, setFlagErrors] = useState({});
  const [hoveredGroup, setHoveredGroup] = useState(null);

  // Auto-expand menus based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Check if we're in a group sub-menu
    const groupSubMenus = ['/group-administrators', '/live-adverts', '/advert-library', '/events', '/group-logs'];
    if (groupSubMenus.some(path => currentPath.startsWith(path))) {
      // Find which group this belongs to and expand it
      const groupId = currentPath.split('/')[2];
      if (groupId) {
        setOpenMenus(prev => ({
          ...prev,
          [`group-${groupId}`]: true
        }));
      }
    }
    
    // Check if we're in settings
    if (currentPath.startsWith('/settings') || currentPath.startsWith('/administrators')) {
      setOpenMenus(prev => ({
        ...prev,
        settings: true
      }));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (user) {
      console.log('User data:', user);
      console.log('Groups:', user.groups);
    }
  }, [user]);

  const toggleMenu = (key) => {
    setOpenMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path) => {
    if (path === '/dashboard' && location.pathname === '/dashboard') return true;
    if (path === '/organisations' && location.pathname === '/organisations') return true;
    if (path === '/settings' && location.pathname.startsWith('/settings')) return true;
    if (path === '/administrators' && location.pathname === '/administrators') return true;
    if (path === '/logs' && location.pathname === '/logs') return true;
    if (path === '/wikiwaste-search' && location.pathname === '/wikiwaste-search') return true;
    // Check if current path starts with the path (for dynamic routes)
    if (path.includes('/:') && location.pathname.startsWith(path.split('/:')[0])) return true;
    return false;
  };

  const isGroupSubMenuActive = (basePath, groupId) => {
    const fullPath = `${basePath}/${groupId}`;
    return location.pathname === fullPath;
  };

  // Get groups from user data and sort alphabetically by country then group name
  const groups = user?.groups || [];
  const sortedGroups = [...groups].sort((a, b) =>
    (a.group_name || '').localeCompare(b.group_name || '')
  );

  const totalGroups = sortedGroups.length;

  const FLAG_BASE_URL = 'https://525482.8664.wiki/app/uploads/flags/';

  const handleFlagError = (groupId) => {
    setFlagErrors(prev => ({ ...prev, [groupId]: true }));
  };

  const mainMenuItems = [
    {
      key: 'settings',
      label: 'Settings',
      icon: <Settings />,
      expandable: true,
      children: [
        { key: 'administrators', label: 'Administrators', icon: <AdminPanelSettings />, link: '/administrators' }
      ]
    },
    {
      key: 'logs',
      label: 'Logs',
      icon: <History />,
      link: '/logs'
    },
    {
      key: 'wikiwaste-search',
      label: 'Wikiwaste Search',
      icon: <Search />,
      link: '/wikiwaste-search'
    }
  ];

  const groupSubMenus = [
    { key: 'administrators', label: 'Group Administrators', icon: <AdminPanelSettings />, link: '/group-administrators' },
    { key: 'live-adverts', label: 'Live Adverts', icon: <LiveTv />, link: '/live-adverts' },
    { key: 'advert-library', label: 'Advert Library', icon: <LibraryBooks />, link: '/advert-library' },
    { key: 'events', label: 'Events', icon: <Event />, link: '/events' },
    { key: 'logs', label: 'Logs', icon: <History />, link: '/group-logs' }
  ];

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        display: { xs: 'none', sm: 'block' },
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
          borderRight: '1px solid #e2e8f0',
          overflowX: 'hidden',
          overflowY: 'hidden',
          marginTop: '64px',
          height: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Main Menu Items */}
      <List sx={{ px: 1, pt: 1 }}>
        {mainMenuItems.map((item) => {
          if (item.expandable) {
            const isSettingsOpen = openMenus.settings || false;
            return (
              <Box key={item.key}>
                <ListItemButton 
                  onClick={() => toggleMenu('settings')}
                  sx={{ 
                    borderRadius: 2, 
                    mb: 0.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    }
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                  {isSettingsOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={isSettingsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton 
                        key={child.key}
                        component={Link} 
                        to={child.link}
                        selected={isActive(child.link)}
                        sx={{ 
                          pl: 4, 
                          borderRadius: 2,
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(245, 124, 0, 0.08)',
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={child.label}
                          primaryTypographyProps={{ fontSize: '0.85rem' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          }
          
          return (
            <ListItemButton 
              key={item.key}
              component={Link} 
              to={item.link}
              selected={isActive(item.link)}
              sx={{ 
                borderRadius: 2, 
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                }
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ my: 2 }} />

      {/* Groups Section */}
      <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* Fixed header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 1,
          flexShrink: 0
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            GROUPS
          </Typography>
          <Chip
            label={totalGroups}
            size="small"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 20,
              minWidth: 20
            }}
          />
        </Box>

        {/* Scrollable list */}
        <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
        {sortedGroups.length > 0 ? (
          sortedGroups.map((group) => {
            const groupMenuKey = `group-${group.group_id}`;
            const isGroupOpen = openMenus[groupMenuKey] || false;
            const isHovered = hoveredGroup === group.group_id;
            
            const countryName = group.country_name || '';
            const groupName = group.group_name || group.group_name_native || 'Unnamed Group';
            const flagUrl = group.flag_url ? `${FLAG_BASE_URL}${group.flag_url}` : null;
            const hasFlagError = flagErrors[group.group_id];

            return (
              <Box key={group.group_id} sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => toggleMenu(groupMenuKey)}
                  onMouseEnter={() => setHoveredGroup(group.group_id)}
                  onMouseLeave={() => setHoveredGroup(null)}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    bgcolor: isGroupOpen ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.05)',
                      transform: 'translateX(4px)',
                    }
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 48 }}>
                    <Box
                      sx={{
                        position: 'relative',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '2px solid',
                        borderColor: isHovered ? 'primary.main' : 'transparent',
                        boxShadow: isHovered ? '0 0 20px rgba(25, 118, 210, 0.3)' : 'none',
                        transition: 'all 0.3s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.05)',
                        transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                      }}
                    >
                      {flagUrl && !hasFlagError ? (
                        <Box
                          component="img"
                          src={flagUrl}
                          alt={countryName}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          onError={() => handleFlagError(group.group_id)}
                        />
                      ) : (
                        <Flag sx={{ fontSize: 24, color: 'text.secondary' }} />
                      )}
                    </Box>
                  </ListItemAvatar>
                  <ListItemText
                    primary={groupName}
                    secondary={countryName || undefined}
                    primaryTypographyProps={{
                      fontSize: '0.88rem',
                      fontWeight: isGroupOpen ? 600 : 400,
                      color: isHovered ? 'primary.main' : 'text.primary',
                      transition: 'color 0.2s ease-in-out',
                      lineHeight: 1.3,
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      lineHeight: 1.3,
                    }}
                  />
                  {isGroupOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={isGroupOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {groupSubMenus.map((subItem) => (
                      <ListItemButton 
                        key={`${group.group_id}-${subItem.key}`}
                        component={Link}
                        to={`${subItem.link}/${group.group_id}`}
                        selected={isGroupSubMenuActive(subItem.link, group.group_id)}
                        sx={{ 
                          pl: 6, 
                          py: 0.75,
                          borderRadius: 2,
                          transition: 'all 0.2s ease-in-out',
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(25, 118, 210, 0.12)',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(245, 124, 0, 0.08)',
                            transform: 'translateX(4px)',
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={subItem.label}
                          primaryTypographyProps={{ 
                            fontSize: '0.8rem',
                            color: 'text.secondary'
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          })
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No groups assigned
            </Typography>
          </Box>
        )}
        </Box>{/* end scrollable list */}
      </Box>{/* end groups section */}

    </Drawer>
  );
};

export default Sidebar;